// Platform bridge — the ONE place the SPA asks "am I inside a native shell,
// and how do I do X here?" (docs/native.md). On the web every function is
// a no-op or the browser equivalent, so pages call these unconditionally.
//
//   ios / android  Capacitor webview (frontend/ios, frontend/android)
//   macos / windows Tauri v2 webview (frontend/src-tauri)
//   web            a normal browser tab
//
// Plugins are imported lazily so the web bundle's critical path does not
// carry them, and so a missing plugin (older shell) degrades to the web path.
import React from 'react';
import { getIdToken } from './authPlane';
import { kitEnv } from './kitEnv';

const API = kitEnv('UPLOAD_API', process.env.REACT_APP_UPLOAD_API || '').replace(/\/$/, '');

function detect() {
  if (typeof window === 'undefined') return 'web';
  const cap = window.Capacitor;
  if (cap?.isNativePlatform?.()) return cap.getPlatform(); // 'ios' | 'android'
  if (window.__TAURI_INTERNALS__) return /Mac/i.test(navigator.platform || navigator.userAgent) ? 'macos' : 'windows';
  return 'web';
}

export const PLATFORM = detect();
export const IS_NATIVE = PLATFORM !== 'web';
export const IS_MOBILE_SHELL = PLATFORM === 'ios' || PLATFORM === 'android';
export const IS_DESKTOP_SHELL = PLATFORM === 'macos' || PLATFORM === 'windows';

// Open a URL outside the app (system browser). In a webview a plain
// target=_blank either does nothing or hijacks the only window.
export async function openExternal(url) {
  try {
    if (IS_MOBILE_SHELL) {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url });
      return;
    }
    if (IS_DESKTOP_SHELL) {
      const { openUrl } = await import('@tauri-apps/plugin-opener');
      await openUrl(url);
      return;
    }
  } catch (e) {
    console.warn('[native] openExternal fell back to window.open:', e.message);
  }
  window.open(url, '_blank', 'noopener');
}

// <scheme>://host/path?x=y  → /host/path?x=y   (the in-app route).
// https://<instance>/path   → /path             (universal/app links).
export function deepLinkToPath(url) {
  try {
    const u = new URL(url);
    if (/^https?:$/.test(u.protocol)) return `${u.pathname}${u.search}${u.hash}` || '/';
    const host = u.host || u.pathname.replace(/^\/+/, '').split('/')[0];
    const rest = u.host ? u.pathname : u.pathname.replace(/^\/+[^/]*/, '');
    return `/${host}${rest}${u.search}${u.hash}`.replace(/\/+/g, '/');
  } catch {
    return null;
  }
}

// Subscribe to deep links while the app is running (and the one that
// launched it, on desktop). `cb(path)` receives an in-app path. Returns an
// unsubscribe function.
export function onDeepLink(cb) {
  let off = () => {};
  let dead = false;
  (async () => {
    try {
      if (IS_MOBILE_SHELL) {
        const { App } = await import('@capacitor/app');
        const h = await App.addListener('appUrlOpen', ({ url }) => { const p = deepLinkToPath(url); if (p) cb(p); });
        const launch = await App.getLaunchUrl().catch(() => null);
        if (launch?.url) { const p = deepLinkToPath(launch.url); if (p) cb(p); }
        if (dead) h.remove(); else off = () => h.remove();
      } else if (IS_DESKTOP_SHELL) {
        const { onOpenUrl, getCurrent } = await import('@tauri-apps/plugin-deep-link');
        const un = await onOpenUrl((urls) => { for (const u of urls) { const p = deepLinkToPath(u); if (p) cb(p); } });
        const current = await getCurrent().catch(() => null);
        for (const u of current || []) { const p = deepLinkToPath(u); if (p) cb(p); }
        if (dead) un(); else off = un;
      }
    } catch (e) {
      console.warn('[native] deep links unavailable:', e.message);
    }
  })();
  return () => { dead = true; off(); };
}

// Register this install for push (mobile only — desktop shells have no push
// transport; use notify() from a live subscription instead). Stores the
// token on the instance via POST /native/device. Resolves to the token or
// null when unavailable / denied.
export async function registerForPush() {
  if (!IS_MOBILE_SHELL || !API) return null;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') perm = await PushNotifications.requestPermissions();
    if (perm.receive !== 'granted') return null;
    const token = await new Promise((resolve, reject) => {
      PushNotifications.addListener('registration', (t) => resolve(t.value));
      PushNotifications.addListener('registrationError', (e) => reject(new Error(e.error || 'registration failed')));
      PushNotifications.register();
    });
    const { App } = await import('@capacitor/app');
    const info = await App.getInfo().catch(() => ({}));
    const idToken = await getIdToken();
    await fetch(`${API}/native/device`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
      body: JSON.stringify({ token, platform: PLATFORM, appId: info.id || '', appVersion: info.version || '' }),
    });
    return token;
  } catch (e) {
    console.warn('[native] push registration failed:', e.message);
    return null;
  }
}

// Forget this install's push token (call on sign-out).
export async function unregisterPush(token) {
  if (!token || !API) return;
  const idToken = await getIdToken();
  await fetch(`${API}/native/device`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
    body: JSON.stringify({ token }),
  }).catch(() => {});
}

// Post a local notification. Mobile → LocalNotifications; desktop → the OS
// notification center; web → the Notification API when permitted.
export async function notify(title, body = '') {
  try {
    if (IS_MOBILE_SHELL) {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      let perm = await LocalNotifications.checkPermissions();
      if (perm.display !== 'granted') perm = await LocalNotifications.requestPermissions();
      if (perm.display !== 'granted') return false;
      await LocalNotifications.schedule({ notifications: [{ id: Date.now() % 2147483647, title, body }] });
      return true;
    }
    if (IS_DESKTOP_SHELL) {
      const n = await import('@tauri-apps/plugin-notification');
      let ok = await n.isPermissionGranted();
      if (!ok) ok = (await n.requestPermission()) === 'granted';
      if (!ok) return false;
      n.sendNotification({ title, body });
      return true;
    }
    if (typeof Notification !== 'undefined') {
      if (Notification.permission === 'default') await Notification.requestPermission();
      if (Notification.permission === 'granted') { new Notification(title, { body }); return true; } // eslint-disable-line no-new
    }
  } catch (e) {
    console.warn('[native] notify failed:', e.message);
  }
  return false;
}

// Mount-once shell integration, called from App: tags <html> for CSS
// (safe-area padding, no text-selection on chrome), routes deep links, and
// sends target=_blank anchors to the system browser.
export function useNativeShell(navigate) {
  React.useEffect(() => {
    if (!IS_NATIVE) return undefined;
    document.documentElement.dataset.native = PLATFORM;
    const offLinks = onDeepLink((path) => navigate(path));
    const onClick = (ev) => {
      const a = ev.target?.closest?.('a[href]');
      if (!a || a.target !== '_blank') return;
      const href = a.getAttribute('href') || '';
      if (!/^https?:/i.test(href) || href.startsWith(window.location.origin)) return;
      ev.preventDefault();
      openExternal(href);
    };
    document.addEventListener('click', onClick, true);
    return () => { offLinks(); document.removeEventListener('click', onClick, true); delete document.documentElement.dataset.native; };
  }, [navigate]);
}
